<template>
  <div class="estoque-realtime-panel">
    <template v-if="produto">
      <div class="text-subtitle-1 font-weight-bold d-flex align-center mb-2">
        <v-icon size="20" class="mr-2 text-primary">mdi-package-variant-closed</v-icon>
        Panorama de Estoque Real-time
      </div>

      <div class="produto-panorama-title mb-4">
        <span class="produto-panorama-codigo">{{ produto.codigo_produto || produto.item_codigo }}</span>
        <span class="produto-panorama-nome">{{ produto.nome_produto || produto.item_descricao }}</span>
      </div>

      <div class="empresa-saldo-row mb-3">
        <span class="empresa-saldo-title">Saldo Geral da Empresa</span>
        <span class="empresa-saldo-item has-help" tabindex="0">
          SAP-FISICO: {{ formatarNumero(totalEstoqueSap) }}
          <v-tooltip activator="parent" location="top">
            Fisico: {{ formatarNumero(totalEstoqueSap) }} | Reservado:
            {{ formatarNumero(totalSapReservado) }} | Disponivel:
            {{ formatarNumero(totalSapDisponivel) }}
          </v-tooltip>
        </span>
        <span class="empresa-saldo-item" :class="totalEstoqueGestor >= 0 ? 'text-success' : 'text-error'">
          GESTOR: {{ formatarNumeroComSinal(totalEstoqueGestor) }}
        </span>
        <span class="empresa-saldo-item text-primary">
          CALCULADO: {{ formatarNumero(totalEstoqueGeral) }}
        </span>
      </div>

      <div class="panorama-card pr-1">
        <v-card v-if="loading" class="d-flex align-center justify-center pa-10" variant="text">
          <v-progress-circular indeterminate color="primary" />
        </v-card>

        <v-expansion-panels v-else v-model="panelsExpanded" variant="accordion" class="custom-panels">
          <v-expansion-panel v-for="dep in depositosVisiveis" :key="dep.codigo_deposito" class="mb-2 rounded-lg border">
            <v-expansion-panel-title class="py-2">
              <template #default="{ expanded }">
                <div class="stock-title-grid">
                  <div class="d-flex align-center min-w-0">
                    <v-icon :color="saldoSapDeposito(dep) > 0 ? 'success' : 'grey'" class="mr-2">
                      {{ saldoSapDeposito(dep) > 0 ? 'mdi-warehouse' : 'mdi-warehouse-off' }}
                    </v-icon>
                    <span class="dep-title-name" :class="expanded ? 'text-primary' : ''">
                      {{ dep.nome_deposito || `Deposito ${dep.codigo_deposito || '-'}` }}
                    </span>
                    <v-chip v-if="isSomenteGestor(dep)" color="warning" variant="tonal" size="x-small" class="ml-2">
                      Somente Gestor
                    </v-chip>
                  </div>

                  <div class="d-flex align-center stock-line-values">
                    <div class="text-right stock-col">
                      <div class="stock-col-label text-grey-darken-1 has-help" tabindex="0">
                        SAP
                        <v-tooltip activator="parent" location="top">
                          Fisico: {{ formatarNumero(saldoSapDeposito(dep)) }} | Reservado:
                          {{ formatarNumero(dep.sap_reservado) }} | Disponivel:
                          {{ formatarNumero(dep.sap_disponivel) }}
                        </v-tooltip>
                      </div>
                      <div class="stock-value text-grey-darken-1">{{ formatarNumero(saldoSapDeposito(dep)) }}</div>
                    </div>

                    <div class="text-right stock-col">
                      <div class="stock-col-label text-warning">GESTOR</div>
                      <div class="stock-value" :class="saldoGestorItem(dep) >= 0 ? 'text-success' : 'text-error'">
                        {{ formatarNumeroComSinal(saldoGestorItem(dep)) }}
                      </div>
                    </div>

                    <div class="text-right stock-col">
                      <div class="stock-col-label text-primary">CALCULADO</div>
                      <div class="stock-value text-primary">{{ formatarNumero(saldoCalculadoItem(dep)) }}</div>
                    </div>
                  </div>
                </div>
              </template>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <v-list density="compact" class="pa-0">
                <v-list-item
                  v-for="loc in localizacoesVisiveis(dep)"
                  :key="`${dep.codigo_deposito}-${codigoLocalizacao(loc) || 'sem-localizacao'}`"
                  class="px-2 border-bottom"
                >
                  <template #prepend>
                    <v-icon size="16" color="primary">mdi-map-marker-outline</v-icon>
                  </template>

                  <v-list-item-title class="text-caption">
                    {{ codigoLocalizacao(loc) || 'Sem localizacao' }}
                    <v-chip v-if="isSomenteGestor(loc, 'localizacao')" color="warning" variant="tonal" size="x-small" class="ml-1">
                      Sem SAP
                    </v-chip>
                    <span class="text-grey-darken-1 ml-1">
                      (Rua: {{ loc.rua || '-' }} / P: {{ loc.prateleira || '-' }})
                    </span>
                  </v-list-item-title>

                  <template #append>
                    <div class="d-flex align-center stock-line-values">
                      <div class="text-right stock-col">
                        <div class="stock-col-label text-grey-darken-1 has-help" tabindex="0">
                          SAP
                          <v-tooltip activator="parent" location="top">
                            Fisico: {{ formatarNumero(saldoSapLocalizacao(loc)) }} | Reservado:
                            {{ formatarNumero(sapReservadoItem(loc)) }} | Disponivel:
                            {{ formatarNumero(sapDisponivelItem(loc)) }}
                          </v-tooltip>
                        </div>
                        <div class="stock-value text-grey-darken-1">{{ formatarNumero(saldoSapLocalizacao(loc)) }}</div>
                      </div>

                      <div class="text-right stock-col">
                        <div class="stock-col-label text-warning">GESTOR</div>
                        <div class="stock-value" :class="saldoGestorItem(loc) >= 0 ? 'text-success' : 'text-error'">
                          {{ formatarNumeroComSinal(saldoGestorItem(loc)) }}
                        </div>
                      </div>

                      <div class="text-right stock-col">
                        <div class="stock-col-label text-primary">CALCULADO</div>
                        <div class="stock-value text-primary">{{ formatarNumero(saldoCalculadoItem(loc, 'localizacao')) }}</div>
                      </div>
                    </div>
                  </template>
                </v-list-item>

                <div v-if="localizacoesVisiveis(dep).length === 0" class="pa-4 text-center text-caption text-grey">
                  Nenhum saldo por endereco registrado.
                </div>
              </v-list>

              <v-btn
                v-if="showSelect && !isSameDep(selectedDeposito, dep.codigo_deposito)"
                block
                size="x-small"
                color="primary"
                variant="tonal"
                class="mt-2"
                prepend-icon="mdi-check"
                @click="$emit('selecionar-deposito', dep)"
              >
                Selecionar para {{ selectLabel }}
              </v-btn>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </template>

    <div v-else class="d-flex flex-column align-center justify-center fill-height pa-10 text-center text-grey">
      <v-icon size="64" class="mb-4 opacity-20">mdi-archive-search-outline</v-icon>
      <div class="text-h6 opacity-50 font-weight-light">Aguardando Selecao de Produto</div>
      <div class="text-caption opacity-40">O panorama de estoques aparecera aqui apos identificar o item</div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EstoqueRealtimePanel",
  props: {
    produto: { type: Object, default: null },
    depositos: { type: Array, default: () => [] },
    localizacoes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    showSelect: { type: Boolean, default: false },
    selectedDeposito: { type: [String, Number], default: null },
    selectLabel: { type: String, default: "Origem" },
  },
  emits: ["selecionar-deposito"],
  data() {
    return {
      panelsExpanded: 0,
    };
  },
  computed: {
    depositosVisiveis() {
      return (this.depositos || []).filter(dep =>
        this.saldoCalculadoItem(dep) > 0 || this.saldoSapDeposito(dep) > 0 || this.saldoGestorItem(dep) !== 0
      );
    },
    totalEstoqueSap() {
      return this.depositosVisiveis.reduce((acc, dep) => acc + this.saldoSapDeposito(dep), 0);
    },
    totalSapReservado() {
      return this.depositosVisiveis.reduce((acc, dep) => acc + parseFloat(dep.sap_reservado || 0), 0);
    },
    totalSapDisponivel() {
      return this.depositosVisiveis.reduce((acc, dep) => acc + parseFloat(dep.sap_disponivel || dep.estoque_disponivel || 0), 0);
    },
    totalEstoqueGestor() {
      return this.depositosVisiveis.reduce((acc, dep) => acc + this.saldoGestorItem(dep), 0);
    },
    totalEstoqueGeral() {
      return this.depositosVisiveis.reduce((acc, dep) => acc + this.saldoCalculadoItem(dep), 0);
    },
  },
  methods: {
    formatarNumero(valor) {
      if (valor === null || valor === undefined || valor === "") return "0.00";
      return parseFloat(valor || 0).toFixed(2);
    },
    formatarNumeroComSinal(valor) {
      const n = parseFloat(valor || 0);
      const abs = Math.abs(n).toFixed(2);
      return n > 0 ? `+${abs}` : n < 0 ? `-${abs}` : "0.00";
    },
    saldoGestorItem(item) {
      return parseFloat(item?.entradas_pendentes || 0) - parseFloat(item?.saidas_pendentes || 0);
    },
    saldoSapDeposito(dep) {
      return parseFloat(dep?.estoque_total ?? dep?.estoque_fisico ?? dep?.estoque_disponivel ?? 0);
    },
    saldoSapLocalizacao(loc) {
      return parseFloat(loc?.estoque_localizacao ?? loc?.quantidade_sap ?? loc?.estoque_fisico ?? 0);
    },
    saldoCalculadoItem(item, nivel = "deposito") {
      if (item?.saldo_projetado !== undefined && item?.saldo_projetado !== null) {
        return parseFloat(item.saldo_projetado || 0);
      }
      const sap = nivel === "localizacao" ? this.saldoSapLocalizacao(item) : this.saldoSapDeposito(item);
      return sap + this.saldoGestorItem(item || {});
    },
    isSomenteGestor(item, nivel = "deposito") {
      const sap = nivel === "localizacao" ? this.saldoSapLocalizacao(item) : this.saldoSapDeposito(item);
      return sap <= 0 && this.saldoGestorItem(item) !== 0 && this.saldoCalculadoItem(item, nivel) !== 0;
    },
    isSameDep(id1, id2) {
      if (!id1 || !id2) return false;
      return this.normalizarDeposito(id1) === this.normalizarDeposito(id2);
    },
    normalizarDeposito(id) {
      const valor = String(id ?? "").trim().toUpperCase();
      const semZeros = valor.replace(/^0+/, "");
      return semZeros || valor;
    },
    codigoLocalizacao(loc) {
      return loc?.codigo_localizacao || loc?.localizacao || loc?.posicao || "";
    },
    localizacoesVisiveis(dep) {
      const depositoDep = dep.codigo_deposito || dep.id_deposito || dep.deposito;
      return (this.localizacoes || []).filter(loc =>
        this.isSameDep(loc.codigo_deposito || loc.id_deposito || loc.deposito, depositoDep) &&
        (this.saldoSapLocalizacao(loc) > 0 || this.saldoCalculadoItem(loc, "localizacao") > 0 || this.saldoGestorItem(loc) !== 0)
      );
    },
    sapReservadoItem(item) {
      return parseFloat(item?.estoque_comprometido || item?.comprometido || item?.sap_reservado || 0);
    },
    sapDisponivelItem(item) {
      const fisico = this.saldoSapLocalizacao(item);
      const reservado = this.sapReservadoItem(item);
      return parseFloat(item?.estoque_disponivel || item?.disponivel || (fisico - reservado) || 0);
    },
  },
};
</script>

<style scoped>
.panorama-card {
  min-height: 500px !important;
  max-height: 600px;
  overflow-y: auto;
}

.produto-panorama-title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}

.produto-panorama-codigo {
  font-size: 1.2rem;
  font-weight: 900;
  color: #1f2937;
}

.produto-panorama-nome {
  font-size: 1.05rem;
  font-weight: 700;
  color: #4b5563;
}

.empresa-saldo-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
}

.empresa-saldo-title,
.empresa-saldo-item {
  font-size: 0.82rem;
  font-weight: 800;
  white-space: nowrap;
}

.stock-title-grid {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) auto;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
}

.dep-title-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 1rem;
  font-weight: 800;
}

.stock-line-values {
  gap: 12px;
  flex-shrink: 0;
}

.stock-col {
  min-width: 78px;
}

.stock-col-label {
  font-size: 0.68rem;
  line-height: 1;
  font-weight: 900;
  white-space: nowrap;
}

.stock-value {
  font-size: 0.78rem;
  line-height: 1.15;
  font-weight: 900;
  white-space: nowrap;
}

.min-w-0 {
  min-width: 0;
}

@media (max-width: 960px) {
  .stock-title-grid {
    grid-template-columns: 1fr;
  }

  .stock-line-values {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
